import { useMemo, useRef } from 'react';
import { flexRender, type Table as TanstackTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Settings2 } from 'lucide-react';
import type { CampaignDto } from '@/generated/korza-api-client/types.gen';
import type { CampaignsTableFooterValues } from '@/hooks/use-campaigns-table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils';

const RIGHT_ALIGNED_COLUMN_IDS = new Set([
  'revenue',
  'paidOrderCount',
  'conversionRate',
  'impressions',
  'clickCount',
  'ctr',
  'actions',
]);

const CENTER_ALIGNED_COLUMN_IDS = new Set(['select']);

const COLUMN_GRID_TEMPLATES: Record<string, string> = {
  select: '44px',
  launchDateTime: '180px',
  audience: 'minmax(140px, 200px)',
  name: 'minmax(200px, 1fr)',
  revenue: '160px',
  paidOrderCount: '120px',
  conversionRate: '180px',
  impressions: '120px',
  clickCount: '120px',
  ctr: '110px',
  actions: '52px',
};
const DEFAULT_GRID_TEMPLATE = '120px';

const STICKY_ACTIONS_HEADER_GRADIENT_CLASS =
  'bg-gradient-to-r from-background/0 from-0% via-background/80 via-25% to-background to-45%';
const STICKY_SELECT_HEADER_GRADIENT_CLASS =
  'bg-gradient-to-l from-background/0 from-0% via-background/80 via-25% to-background to-45%';
const STICKY_ACTIONS_BODY_GRADIENT_CLASS =
  'bg-gradient-to-r from-background/0 from-0% via-background/80 via-25% to-background to-45% group-hover:via-[color-mix(in_oklab,hsl(var(--muted))_50%,hsl(var(--background)))] group-hover:to-[color-mix(in_oklab,hsl(var(--muted))_50%,hsl(var(--background)))]';
const STICKY_SELECT_BODY_GRADIENT_CLASS =
  'bg-gradient-to-l from-background/0 from-0% via-background/80 via-25% to-background to-45% group-hover:via-[color-mix(in_oklab,hsl(var(--muted))_50%,hsl(var(--background)))] group-hover:to-[color-mix(in_oklab,hsl(var(--muted))_50%,hsl(var(--background)))]';
const STICKY_SELECT_FOOTER_GRADIENT_CLASS =
  'bg-gradient-to-l from-muted/0 from-0% to-muted to-45%';
const STICKY_ACTIONS_FOOTER_GRADIENT_CLASS =
  'bg-gradient-to-r from-muted/0 from-0% to-muted to-45%';

const HEADER_CELL_BASE =
  'flex items-center h-12 px-4 text-sm font-medium text-muted-foreground bg-background';
const BODY_CELL_BASE = 'flex items-center px-4 py-4 align-middle text-sm';
const FOOTER_CELL_BASE =
  'flex items-center h-12 px-4 text-sm font-medium tabular-nums bg-muted';

function getHeaderCellClassName(columnId: string) {
  return cn(
    HEADER_CELL_BASE,
    CENTER_ALIGNED_COLUMN_IDS.has(columnId) && 'justify-center px-0',
    RIGHT_ALIGNED_COLUMN_IDS.has(columnId) && 'justify-end',
    columnId === 'select' &&
      `sticky left-0 z-40 bg-background ${STICKY_SELECT_HEADER_GRADIENT_CLASS}`,
    columnId === 'actions' &&
      `sticky right-0 z-40 bg-background ${STICKY_ACTIONS_HEADER_GRADIENT_CLASS}`
  );
}

function getBodyCellClassName(columnId: string) {
  return cn(
    BODY_CELL_BASE,
    CENTER_ALIGNED_COLUMN_IDS.has(columnId) && 'justify-center px-0',
    RIGHT_ALIGNED_COLUMN_IDS.has(columnId) && 'justify-end',
    columnId === 'select' &&
      `sticky left-0 z-20 ${STICKY_SELECT_BODY_GRADIENT_CLASS}`,
    columnId === 'actions' &&
      `sticky right-0 z-20 ${STICKY_ACTIONS_BODY_GRADIENT_CLASS}`
  );
}

function getFooterCellClassName(columnId: string) {
  return cn(
    FOOTER_CELL_BASE,
    CENTER_ALIGNED_COLUMN_IDS.has(columnId) && 'justify-center px-0',
    RIGHT_ALIGNED_COLUMN_IDS.has(columnId) && 'justify-end',
    columnId === 'select' &&
      `sticky left-0 z-40 ${STICKY_SELECT_FOOTER_GRADIENT_CLASS}`,
    columnId === 'actions' &&
      `sticky right-0 z-40 ${STICKY_ACTIONS_FOOTER_GRADIENT_CLASS}`
  );
}

export type CampaignsTableDisplayLabels = {
  columns: string;
  noCampaignsFound: string;
};

export type CampaignsTableColumnMenuLabels = {
  columns: string;
};

export function CampaignsTableColumnVisibilityMenu({
  table,
  analyticsEnabled,
  labels,
}: {
  table: TanstackTable<CampaignDto>;
  analyticsEnabled: boolean;
  labels: CampaignsTableColumnMenuLabels;
}) {
  const hideableColumns = table
    .getAllLeafColumns()
    .filter((column) => column.getCanHide());

  if (!analyticsEnabled || hideableColumns.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="shrink-0">
          <Settings2 className="h-4 w-4" aria-hidden />
          {labels.columns}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>{labels.columns}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {hideableColumns.map((column) => (
          <DropdownMenuCheckboxItem
            key={column.id}
            checked={column.getIsVisible()}
            onCheckedChange={(value) => column.toggleVisibility(!!value)}
            onSelect={(e) => e.preventDefault()}
          >
            {(column.columnDef.meta as { columnLabel?: string } | undefined)
              ?.columnLabel ?? column.id}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface CampaignsTableProps {
  table: TanstackTable<CampaignDto>;
  labels: CampaignsTableDisplayLabels;
  footerValues: CampaignsTableFooterValues | null;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

const ROW_ESTIMATED_HEIGHT = 57;

export function CampaignsTable({
  table,
  labels,
  footerValues,
  page,
  pageSize,
  total,
  onPageChange,
}: CampaignsTableProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const visibleLeafColumns = table.getVisibleLeafColumns();
  const { rows } = table.getRowModel();

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const renderPaginationItems = () => {
    if (totalPages <= 1) return null;

    const items = [];

    items.push(
      <PaginationItem key="prev">
        <PaginationPrevious
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (page > 1) onPageChange(page - 1);
          }}
          className={page === 1 ? 'pointer-events-none opacity-50' : ''}
        />
      </PaginationItem>
    );

    let startPage = Math.max(1, page - 1);
    let endPage = Math.min(totalPages, page + 1);

    if (page === 1) {
      endPage = Math.min(totalPages, 3);
    }
    if (page === totalPages && totalPages > 2) {
      startPage = Math.max(1, totalPages - 2);
    }

    if (startPage > 1) {
      items.push(
        <PaginationItem key={1}>
          <PaginationLink
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onPageChange(1);
            }}
          >
            1
          </PaginationLink>
        </PaginationItem>
      );
      if (startPage > 2) {
        items.push(<PaginationEllipsis key="start-ellipsis" />);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onPageChange(i);
            }}
            isActive={i === page}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<PaginationEllipsis key="end-ellipsis" />);
      }
      items.push(
        <PaginationItem key={totalPages}>
          <PaginationLink
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onPageChange(totalPages);
            }}
          >
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }

    items.push(
      <PaginationItem key="next">
        <PaginationNext
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (page < totalPages) onPageChange(page + 1);
          }}
          className={
            page === totalPages ? 'pointer-events-none opacity-50' : ''
          }
        />
      </PaginationItem>
    );

    return items;
  };

  const gridTemplateColumns = useMemo(
    () =>
      visibleLeafColumns
        .map(
          (column) => COLUMN_GRID_TEMPLATES[column.id] ?? DEFAULT_GRID_TEMPLATE
        )
        .join(' '),
    [visibleLeafColumns]
  );

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_ESTIMATED_HEIGHT,
    overscan: 8,
    getItemKey: (index) => rows[index].id,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  const footerValueByColumn: Record<string, React.ReactNode> = footerValues
    ? {
        revenue: (
          <>
            <span className="text-xs text-muted-foreground mr-1">TOT</span>
            {footerValues.revenueSum}
          </>
        ),
        paidOrderCount: (
          <>
            <span className="text-xs text-muted-foreground mr-1">TOT</span>
            {footerValues.paidOrdersSum}
          </>
        ),
        impressions: (
          <>
            <span className="text-xs text-muted-foreground mr-1">TOT</span>
            {footerValues.impressionsSum}
          </>
        ),
        clickCount: (
          <>
            <span className="text-xs text-muted-foreground mr-1">TOT</span>
            {footerValues.clicksSum}
          </>
        ),
        conversionRate: (
          <>
            <span className="text-xs text-muted-foreground mr-1">MED</span>
            {footerValues.conversionRateAvg}
          </>
        ),
        ctr: (
          <>
            <span className="text-xs text-muted-foreground mr-1">MED</span>
            {footerValues.ctrAvg}
          </>
        ),
      }
    : {};

  return (
    <div className="flex flex-col gap-4 min-h-0 max-h-full">
      <div
        ref={scrollRef}
        className="rounded-md border overflow-auto max-w-full max-h-full min-h-0"
        role="table"
      >
        <div className="min-w-full">
          <div
            role="rowgroup"
            className="sticky top-0 z-30 bg-background shadow-[inset_0_-1px_0_0_hsl(var(--border))]"
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <div
                key={headerGroup.id}
                role="row"
                className="border-b"
                style={{ display: 'grid', gridTemplateColumns }}
              >
                {headerGroup.headers.map((header) => (
                  <div
                    key={header.id}
                    role="columnheader"
                    className={getHeaderCellClassName(header.column.id)}
                    aria-sort={
                      header.column.getIsSorted() === 'asc'
                        ? 'ascending'
                        : header.column.getIsSorted() === 'desc'
                          ? 'descending'
                          : header.column.getCanSort()
                            ? 'none'
                            : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </div>
                ))}
              </div>
            ))}
          </div>

          {rows.length === 0 ? (
            <div role="row" className="py-10 text-center text-muted-foreground">
              <div role="cell">{labels.noCampaignsFound}</div>
            </div>
          ) : (
            <div
              role="rowgroup"
              style={{ height: `${totalSize}px`, position: 'relative' }}
            >
              {virtualItems.map((virtualRow) => {
                const row = rows[virtualRow.index];
                return (
                  <div
                    key={virtualRow.key}
                    role="row"
                    data-index={virtualRow.index}
                    ref={virtualizer.measureElement}
                    className="group border-b hover:bg-[color-mix(in_oklab,hsl(var(--muted))_50%,hsl(var(--background)))]"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      minWidth: scrollRef.current?.scrollWidth,
                      transform: `translateY(${virtualRow.start}px)`,
                      display: 'grid',
                      gridTemplateColumns,
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <div
                        key={cell.id}
                        role="cell"
                        className={getBodyCellClassName(cell.column.id)}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {footerValues && (
            <div
              role="rowgroup"
              className="sticky bottom-0 z-30 bg-background shadow-[inset_0_1px_0_0_hsl(var(--border))]"
            >
              <div role="row" style={{ display: 'grid', gridTemplateColumns }}>
                {visibleLeafColumns.map((column) => (
                  <div
                    key={column.id}
                    role="cell"
                    className={getFooterCellClassName(column.id)}
                  >
                    {footerValueByColumn[column.id] ?? null}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex min-h-10 w-full shrink-0 items-center justify-center overflow-x-auto">
        {totalPages > 1 && (
          <Pagination className="w-max shrink-0">
            <PaginationContent>{renderPaginationItems()}</PaginationContent>
          </Pagination>
        )}
      </div>
    </div>
  );
}
